import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Droplets, Home, MapPin, Calendar, CreditCard } from "lucide-react";
import api from "@/lib/api";

const SERVICE_LABEL = { pool: "Pool Filling", tank: "Tank Refill", construction: "Construction", emergency: "Emergency" };

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const orderIdParam = params.get("order_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let attempts = 0;
    let cancelled = false;

    const fetchOrder = async (id) => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (!cancelled) setOrder(data);
      } catch {}
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (data.order_id) fetchOrder(data.order_id);
          return;
        }
        if (attempts >= 8) { setState("error"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };

    if (orderIdParam) {
      setState("paid");
      fetchOrder(orderIdParam);
    } else if (sessionId) {
      poll();
    } else {
      setState("error");
    }
    return () => { cancelled = true; };
  }, [sessionId, orderIdParam]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-5">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-10 max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-5" />
            <h1 className="font-heading text-xl font-bold text-slate-900">Confirming your payment...</h1>
            <p className="text-slate-500 text-sm mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold text-slate-900" data-testid="payment-success-title">Order Confirmed!</h1>
            <p className="text-slate-500 mt-2">
              {orderIdParam ? "Thanks! We've received your booking and will be in touch to confirm delivery." : "Payment received. Your water delivery is booked."}
            </p>
            {order && (
              <div className="mt-6 bg-slate-50 rounded-2xl p-5 text-left space-y-2.5 text-sm">
                <Row icon={Droplets} label="Volume" value={`${order.litres.toLocaleString()}L — ${SERVICE_LABEL[order.service_type]}`} />
                <Row icon={MapPin} label="Address" value={`${order.address}, ${order.suburb}`} />
                <Row icon={Calendar} label="Date" value={order.scheduled_date} />
                <Row icon={CreditCard} label="Payment" value={order.payment_method === "card" ? `Paid $${order.amount}` : `$${order.amount} on delivery`} />
              </div>
            )}
            <button
              onClick={() => navigate("/")}
              className="brand-gradient btn-shine text-white font-semibold w-full py-3 rounded-full mt-6 flex items-center justify-center gap-2"
              data-testid="payment-success-home-btn"
            >
              <Home className="w-4 h-4" /> Back to Home
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-heading text-xl font-bold text-slate-900">We couldn't confirm your payment</h1>
            <p className="text-slate-500 text-sm mt-2">If you were charged, don't worry — contact us and we'll sort it out.</p>
            <button onClick={() => navigate("/book")} className="brand-gradient text-white font-semibold w-full py-3 rounded-full mt-6" data-testid="payment-retry-btn">
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
    <div>
      <span className="text-slate-400">{label}: </span>
      <span className="text-slate-700 font-medium">{value}</span>
    </div>
  </div>
);
